import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import Card from 'components/card';
import { MdClose, MdOpenInNew, MdDelete, MdAdd, MdDownload, MdUpload, MdPictureAsPdf } from 'react-icons/md';
import * as XLSX from 'xlsx';
import { getPurchase, markReceived, markPartialReceived, updatePurchase, deletePurchase, uploadPurchaseInvoice, getPurchaseInvoiceUrl, cancelPurchaseReception } from 'interfaces/purchases';
import { getSuppliers, getSupplier, getSupplierProducts } from 'interfaces/supplier';
import { toast } from 'react-toastify';
import { updateProductPrice, getProductsByIds, updateProductSellingPrice } from 'interfaces/products';
import { updateSpecificPricing, getAllPricings } from 'interfaces/pricing';
import SaveStatus from "components/actions/SaveStatus";
import { useDisclosure } from '@chakra-ui/hooks';
import DeletePurchase from './DeletePurchase';

interface UpdatePurchaseModalProps {
  id: string | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

// Stable per-row key generator. Used to index local state by line identity
// instead of by array position, so deleting/re-ordering a line can never
// shift one row's data (qty, price, lot, dlc…) onto another.
let __rowKeySeq = 0;
const genRowKey = (): string =>
  `row-${Date.now().toString(36)}-${(__rowKeySeq++).toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const UpdatePurchaseModal: React.FC<UpdatePurchaseModalProps> = ({ id, isOpen, onClose, onUpdated }) => {
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isCancelOpen, onOpen: onCancelOpen, onClose: onCancelClose } = useDisclosure();
  const [po, setPo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [receptionDate, setReceptionDate] = useState('');
  const [tempReception, setTempReception] = useState('');
  const [savingReception, setSavingReception] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markingReceived, setMarkingReceived] = useState(false);
  const [markingPartial, setMarkingPartial] = useState(false);
  const [cancellingReception, setCancellingReception] = useState(false);

  // Partial reception: qty to receive per item (separate from ordered qty)
  // All per-line maps below are keyed by the line's stable __key (see genRowKey).
  const [receivedQuantities, setReceivedQuantities] = useState<{[key:string]: number}>({});

  // Multi-payment state
  const [payments, setPayments] = useState<any[]>([]);
  const [newPayAmount, setNewPayAmount] = useState<string>('');
  const [showPayForm, setShowPayForm] = useState<boolean>(false);
  const [newPayDate, setNewPayDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [newPayMethod, setNewPayMethod] = useState<string>('virement');
  const [newPayNote, setNewPayNote] = useState<string>('');
  const [savingPayment, setSavingPayment] = useState(false);

  const [lotNumbers, setLotNumbers] = useState<{[key:string]: string}>({});
  const [areas, setAreas] = useState<{[key:string]: string}>({});
  const [dlcs, setDlcs] = useState<{[key:string]: string}>({});
  const [quantities, setQuantities] = useState<{[key:string]: number}>({});
  const [prices, setPrices] = useState<{[key:string]: number}>({});
  const [vats, setVats] = useState<{[key:string]: number}>({});
  const [receptionTemps, setReceptionTemps] = useState<{[key:string]: string}>({});
  const [deliveryFees, setDeliveryFees] = useState<number>(0);
  const [distributeFeesToProducts, setDistributeFeesToProducts] = useState<boolean>(false);
  const [feesDistributed, setFeesDistributed] = useState<boolean>(false);

  const [blNumber, setBlNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');

  const [autoSaving, setAutoSaving] = useState(false);
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [priceUpdated, setPriceUpdated] = useState<{ [key:string]: boolean }>({});

  // Add/remove items
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [showAddRow, setShowAddRow] = useState(false);
  const [newProductId, setNewProductId] = useState('');
  const [newProductQty, setNewProductQty] = useState(1);
  const [newProductPrice, setNewProductPrice] = useState(0);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const productSearchRef = useRef<HTMLDivElement>(null);

  // PDF invoice state
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);

  const autoSaveTimer = useRef<number | null>(null);
  const ignoreAutoSaveRef = useRef(false); // when true auto-save won't run

  // Price propagation proposal
  const [priceProposals, setPriceProposals] = useState<Array<{
    key: string;
    productId: string;
    productName: string;
    oldPurchasePrice: number;
    newPurchasePrice: number;
    pct: number;
  }>>([]);
  const [showPriceProposalBanner, setShowPriceProposalBanner] = useState(false);
  const [applyingPriceProposal, setApplyingPriceProposal] = useState(false);
  const priceProposalTimer = useRef<number | null>(null);
  const [customPricingProposals, setCustomPricingProposals] = useState<Array<{
    id: string;
    productId: string;
    productName: string;
    oldPrice: number;
    newPrice: number;
    pct: number;
    tags: string[];
    customerId?: string;
  }>>([]);
  const [applyToCustomPricings, setApplyToCustomPricings] = useState(true);
  const [loadingCustomPricings, setLoadingCustomPricings] = useState(false);
  // Per-product debounce timers for updateProductPrice (avoid firing on every keystroke)
  const productPriceTimers = useRef<Record<string, number>>({});
  // Incremented on each price change to cancel stale getAllPricings fetches
  const pricingFetchGen = useRef(0);

  const hasQtyPriceChanged = useMemo(() => {
    const items = po?.items || [];
    return items.some((it: any) => {
      const k = it.__key;
      const baseQ = Number(it.quantity ?? it.qty ?? 0);
      const baseP = Number(it.price ?? it.unitPrice ?? 0);
      const baseV = Number(it.vat ?? it.vatPurchase ?? 0);
      const curQ = Number(quantities[k] ?? baseQ);
      const curP = Number(prices[k] ?? baseP);
      const curV = Number(vats[k] ?? baseV);
      const norm = (n: number) => (Number.isFinite(n) ? n : 0);
      return norm(curQ) !== norm(baseQ) || norm(curP) !== norm(baseP) || norm(curV) !== norm(baseV);
    });
  }, [po?.items, quantities, prices, vats]);

  const hasDeliveryFeesChanged = useMemo(() => {
    const baseFees = Number(po?.deliveryFees ?? 0);
    const currentFees = Number(deliveryFees ?? 0);
    return baseFees !== currentFees;
  }, [po?.deliveryFees, deliveryFees]);

  const hasMetaChanged = useMemo(() => {
    return blNumber !== (po?.blNumber || '') || invoiceNumber !== (po?.invoiceNumber || '');
  }, [po?.blNumber, po?.invoiceNumber, blNumber, invoiceNumber]);

  const toYMD = (v: any) => {
    if (!v) return '';
    if (typeof v === 'string') return v.slice(0, 10);
    try {
      return new Date(v).toISOString().slice(0, 10);
    } catch {
      return '';
    }
  };

  const formatMoney = (v: any) =>
    Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);

  const getSupplierName = (s: any) => {
    if (!s) return '—';
    if (typeof s === 'string') return s;
    return s.name || s.displayName || s.company || '—';
  };

  const getVatRate = (it: any) => {
    const raw = it?.vat ?? it?.vatPurchase ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const getProdId = (it: any) => {
    const id = it?.productId?._id || it?.productId;
    // Ensure we return a string ID, not an object
    return typeof id === 'string' ? id : (id?._id || id?.toString() || null);
  };

  // Returns true when the product linked to this line has noDlc:true
  const isItemNoDlc = (item: any): boolean => {
    if (item?.productId?.noDlc === true) return true;
    const pid = getProdId(item);
    if (pid) {
      const prod = allProducts.find((p: any) => (p._id || p.id) === pid);
      if (prod?.noDlc === true) return true;
    }
    return false;
  };

  const load = useCallback(async () => {
    if (!id || !isOpen) return;
    setLoading(true);
    try {
      const supRes: any = await getSuppliers();
      setSuppliers(supRes?.docs || supRes?.data || supRes || []);
      const res: any = await getPurchase(id);
      if (!res) {
        toast.error('Bon introuvable');
        setLoading(false);
        return;
      }
      // Assign a stable per-row key (__key) to each line. It backs both the
      // local-state maps and the React key, so editing/deleting/re-ordering a
      // line can never shift data across rows. _id is kept for the backend.
      const items = (res.items || []).map((item: any) => ({
        ...item,
        __key: item.__key || item._id || genRowKey(),
      }));
      setPo({ ...res, items });

      const rec = res.receptionDate;
      const today = new Date().toISOString().split('T')[0];
      setReceptionDate(rec ? new Date(rec).toISOString().split('T')[0] : today);

      setTempReception(res.tempReception ?? '');

      // initialize per-line state safely (keyed by __key)
      const initialLotNumbers: {[key:string]: string} = {};
      const initialDlcs: {[key:string]: string} = {};
      const initialAreas: {[key:string]: string} = {};
      const qts: {[key:string]: number} = {};
      const prs: {[key:string]: number} = {};
      const initialReceptionTemps: {[key:string]: string} = {};
      const vts: {[key:string]: number} = {};
      const initialReceivedQtys: {[key:string]: number} = {};

      items.forEach((item: any) => {
        const k = item.__key;
        initialLotNumbers[k] = item.lotNumber || '';
        initialDlcs[k] = item.dlc ? toYMD(item.dlc) : '';
        initialAreas[k] = item.area || 'zone-a';
        qts[k] = Number(item.quantity ?? item.qty ?? 0) || 0;
        prs[k] = Number(item.price ?? item.unitPrice ?? 0) || 0;
        vts[k] = Number(item.vat ?? item.vatPurchase ?? 0);
        initialReceptionTemps[k] = item.receptionTemp !== undefined ? String(item.receptionTemp) : '';
        const ordered = Number(item.quantity ?? 0);
        const alreadyReceived = Number(item.receivedQuantity ?? 0);
        initialReceivedQtys[k] = Math.max(0, ordered - alreadyReceived);
      });

      setLotNumbers(initialLotNumbers);
      setDlcs(initialDlcs);
      setAreas(initialAreas);
      setQuantities(qts);
      setPrices(prs);
      setVats(vts);
      setReceptionTemps(initialReceptionTemps);
      setDeliveryFees(Number(res.deliveryFees ?? 0));
      setFeesDistributed(res.feesDistributed ?? false);
      setBlNumber(res.blNumber || '');
      setInvoiceNumber(res.invoiceNumber || '');
      setReceivedQuantities(initialReceivedQtys);
      setPayments(Array.isArray(res.payments) ? res.payments : []);

      // when purchase is already received, ensure autosave disabled
      if (res.status === 'received') ignoreAutoSaveRef.current = true;
    } finally {
      setLoading(false);
    }
  }, [id, isOpen]);

  useEffect(() => { load(); }, [load]);

  // Load supplier products for the add-item form
  useEffect(() => {
    if (!isOpen || !po) return;
    (async () => {
      const supplierId =
        typeof po.supplier === 'object'
          ? (po.supplier?._id || po.supplier?.id)
          : (po.supplier || po.supplierId || po.supplier_id);
      if (!supplierId) return;

      const supplier = await getSupplier(supplierId);
      let productIds: string[] =
        Array.isArray(supplier?.products)
          ? supplier.products
          : Array.isArray(supplier?.productIds)
          ? supplier.productIds
          : [];

      if (productIds.length === 0) {
        const legacy = await getSupplierProducts(supplierId);
        if (Array.isArray(legacy) && legacy.length > 0) {
          if (legacy[0] && typeof legacy[0] === 'object' && legacy[0].name) {
            const sorted = [...legacy].sort((a: any, b: any) =>
              (a?.name || '').localeCompare(b?.name || '')
            );
            setAllProducts(sorted);
            return;
          }
          if (legacy.every((v: any) => typeof v === 'string')) {
            productIds = legacy;
          }
        }
      }

      if (productIds.length === 0) { setAllProducts([]); return; }

      const products = await getProductsByIds(productIds);
      products.sort((a: any, b: any) => (a?.name || '').localeCompare(b?.name || ''));
      setAllProducts(products);
    })();
  }, [isOpen, po]);

  useEffect(() => {
    if (!isOpen) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  const supplierName =
    getSupplierName(
      po?.supplier ||
        suppliers.find((s: any) => (s._id) === (po?.supplierId || po?.supplier_id))
    );

  const deliveryPlanned =
    po?.deliveryPurchase ||
    po?.eta ||
    po?.deliveryDate ||
    null;

  // build items payload used by manual saves, mark received etc.
  const buildItemsPayload = () =>
    (po.items || []).map((item: any) => {
      const k = item.__key;
      const q = Number(quantities[k] ?? item.quantity);
      const newPrice = Number(prices[k] ?? item.price);
      const temp = receptionTemps[k];
      return {
        _id: item._id,
        productId: getProdId(item),
        quantity: q,
        price: newPrice,
        vat: vats[k] ?? item.vat ?? item.vatPurchase ?? 0,
        lotNumber: lotNumbers[k] || item.lotNumber || '',
        area: areas[k] || item.area || 'zone-a',
        dlc: isItemNoDlc(item) ? '' : (dlcs[k] ? new Date(dlcs[k]).toISOString() : (item.dlc ? new Date(item.dlc).toISOString() : '')),
        receptionTemp: temp !== '' ? Number(temp) : undefined,
      };
    });

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

  // Function to distribute delivery fees across products
  const handleDistributeFeesToProducts = () => {
    if (!po?.items || deliveryFees <= 0) return;

    // Calculate the total HT amount of the order (excluding fees, only for lines with qty > 0)
    const totalHT = (po.items || []).reduce((sum: number, it: any) => {
      const k = it.__key;
      const qty = Number(quantities[k] ?? it.quantity ?? 0);
      const price = Number(prices[k] ?? it.price ?? 0);
      if (qty <= 0) return sum;
      return sum + (qty * price);
    }, 0);

    if (totalHT === 0) return;

    const newPrices: {[key:string]: number} = { ...prices };
    (po.items || []).forEach((it: any) => {
      const k = it.__key;
      const currentPrice = Number(prices[k] ?? it.price ?? 0);
      const qty = Number(quantities[k] ?? it.quantity ?? 0);
      
      if (qty <= 0) return;
      
      const lineTotal = qty * currentPrice;
      
      const linePercentage = lineTotal / totalHT;
      
      const lineFeesToAdd = deliveryFees * linePercentage;
      
      newPrices[k] = round2(currentPrice + (lineFeesToAdd / qty));
    });

    setPrices(newPrices);
    setDistributeFeesToProducts(false);
    setFeesDistributed(true);
    toast.success('Frais de livraison répartis sur les produits');
  };

  const { totalArticlesHT, totalTVA, totalHT, totalTTC } = useMemo(() => {
    const items = po?.items || [];
    let ht = 0;
    let tva = 0;
    items.forEach((it: any) => {
      const k = it.__key;
      const q = Number(quantities[k] ?? it.quantity);
      const pu = Number(prices[k] ?? it.price);
      const lineHT = (isNaN(q) ? 0 : q) * (isNaN(pu) ? 0 : pu);
      const vatRate = Number(vats[k] ?? getVatRate(it)) / 100;
      const lineTVA = lineHT * (vatRate > 0 ? vatRate : 0);
      ht += lineHT;
      tva += lineTVA;
    });
    const fees = feesDistributed ? 0 : Number(deliveryFees ?? 0);
    const totalHT = round2(ht + (Number.isFinite(fees) ? fees : 0));
    const totalTTC = round2(totalHT + tva);
    return {
      totalArticlesHT: round2(ht),
      totalTVA: round2(tva),
      totalHT,
      totalTTC,
    };
  }, [po?.items, quantities, prices, vats, deliveryFees, feesDistributed]);

  const getMagicToken = (p: any) => {
       if (!p) return null;
       if (p.magicLinkUuid) return p.magicLinkUuid;
       const url = p.magicLink || '';
       if (!url) return null;
       const parts = String(url).split('/');
       return parts[parts.length - 1] || null;
     };
     const magicToken = useMemo(() => getMagicToken(po), [po]);
     const handleOpenMagicLink = () => {
       if (!magicToken) {
         toast.error('Lien fournisseur indisponible');
         return;
       }
       window.open(`/view-purchase/${magicToken}`, '_blank');
     };

  // Sync product prices (manual - kept)
const syncProductsPrices = async () => {
    const items = po?.items || [];

    const updates = items.map(async (item: any, index: number) => {
      const productId = getProdId(item);
      const newPrice = Number(prices[item.__key] ?? item.price);
      const oldPrice = Number(item.price);

      // Validate product ID
      if (!productId || typeof productId !== 'string' || productId.trim() === '') {
        console.warn(`⚠️ ID produit invalide pour l'index ${index}:`, productId);
        return null;
      }
      
      if (!isFinite(newPrice) || newPrice <= 0) return null;
      if (oldPrice === newPrice) return null;

      try {
        await updateProductPrice(productId, newPrice);
        return productId;
      } catch (err) {
        console.warn(`⚠️ Impossible de mettre à jour le produit ${productId}`, err);
        return null;
      }
    });

    await Promise.allSettled(updates);
  };

  // --- Apply selling price % change ---
  const handleApplyPriceProposals = async () => {
    if (!priceProposals.length) return;
    setApplyingPriceProposal(true);
    try {
      // Dedicated PATCH endpoint reads the current product from DB — no stale snapshot risk
      await Promise.all(
        priceProposals.map(({ productId, pct }) => updateProductSellingPrice(productId, pct)),
      );
      // Also update custom pricings if checkbox is checked
      if (applyToCustomPricings && customPricingProposals.length > 0) {
        await Promise.allSettled(
          customPricingProposals.map((cp) =>
            updateSpecificPricing(cp.id, { price: cp.newPrice, productId: cp.productId, tags: cp.tags }),
          ),
        );
      }
      const customCount = applyToCustomPricings ? customPricingProposals.length : 0;
      toast.success(`Prix de vente mis à jour${customCount > 0 ? ` + ${customCount} tarif(s) personnalisé(s)` : ''}`);
      // Sync po.items baseline prices so the proposal detector no longer sees these as "changed"
      // (avoids the banner re-appearing when the next product price is edited)
      setPo((prev: any) => {
        if (!prev?.items) return prev;
        const updatedItems = prev.items.map((item: any) => {
          const applied = priceProposals.find((p) => p.key === item.__key);
          if (!applied) return item;
          return { ...item, price: prices[item.__key] ?? item.price };
        });
        return { ...prev, items: updatedItems };
      });
    } catch (err) {
      toast.error('Erreur lors de la mise à jour des prix de vente');
    } finally {
      setApplyingPriceProposal(false);
      setShowPriceProposalBanner(false);
      setPriceProposals([]);
      setCustomPricingProposals([]);
    }
  };

  // --- AUTO-SAVE ---
  useEffect(() => {
    if (!po || !isOpen) return;
    if (ignoreAutoSaveRef.current) return;
    if (!hasQtyPriceChanged && !hasDeliveryFeesChanged && !hasMetaChanged) return;

    const timer = setTimeout(async () => {
      try {
        setAutoSaving(true);
        setAutoSaveError(null);

        // Build payload items by only modifying what needs to be changed
        const itemsPatch = (po.items || []).map((item: any) => {
          const k = item.__key;
          const quantity = Number(quantities[k] ?? item.quantity ?? 0);
          const price = Number(prices[k] ?? item.price ?? 0);
          const vat = vats[k] ?? item.vat ?? item.vatPurchase ?? 0;
          const temp = receptionTemps[k];
          return {
            ...item,
            productId: getProdId(item),
            quantity,
            price,
            vat,
            lotNumber: lotNumbers[k] ?? item.lotNumber ?? '',
            area: areas[k] ?? item.area ?? 'zone-a',
            dlc: isItemNoDlc(item) ? '' : (dlcs[k] ? new Date(dlcs[k]).toISOString() : item.dlc ?? ''),
            receptionTemp: temp !== '' ? Number(temp) : undefined,
          };
        });


        // Calculate totalHT and totalPrice (TTC)
        const itemsHT = itemsPatch.reduce((sum: any, it: any) => sum + (Number(it.quantity) * Number(it.price)), 0);
        const totalVAT = itemsPatch.reduce((sum: any, it: any) => sum + ((Number(it.quantity) * Number(it.price)) * (Number(it.vat ?? 0) / 100)), 0);
        // Add delivery fees only if not distributed (otherwise already included in unit prices)
        const fees = feesDistributed ? 0 : Number(deliveryFees ?? 0);
        const totalHT = round2(itemsHT + (Number.isFinite(fees) ? fees : 0));
        const totalPrice = round2(totalHT + totalVAT);

        const payload: any = {
          _id: po._id,
          items: itemsPatch,
          deliveryFees: Number(deliveryFees ?? 0),
          feesDistributed: feesDistributed,
          totalHT: round2(totalHT),
          totalPrice: round2(totalPrice),
          blNumber,
          invoiceNumber,
        };

        const updated = await updatePurchase(payload);
        if (!updated || updated.error) throw new Error('update failed');

        setLastSavedAt(Date.now());
        onUpdated?.();
      } catch (e) {
        setAutoSaveError('Erreur de sauvegarde');
      } finally {
        setAutoSaving(false);
      }
    }, 700);

    return () => clearTimeout(timer);
  }, [hasQtyPriceChanged, hasDeliveryFeesChanged, hasMetaChanged, po?._id, isOpen, quantities, prices, vats, lotNumbers, areas, dlcs, receptionTemps, deliveryFees, feesDistributed, blNumber, invoiceNumber, onUpdated]);


  useEffect(() => {
    if (!po || !prices) return;

    // Debounce per-product purchase price update (700ms) to avoid N calls per keystroke
    Object.keys(prices).forEach((key) => {
      const item = (po.items || []).find((it: any) => it.__key === key);
      if (!item) return;
      const newPrice = Number(prices[key]);
      const oldPrice = Number(item.price ?? 0);
      if (newPrice === oldPrice || isNaN(newPrice)) return;

      if (productPriceTimers.current[key]) window.clearTimeout(productPriceTimers.current[key]);
      productPriceTimers.current[key] = window.setTimeout(async () => {
        const productId = getProdId(item);
        if (!productId || typeof productId !== 'string' || productId.trim() === '') return;
        try {
          await updateProductPrice(productId, newPrice);
          setPriceUpdated(prev => ({ ...prev, [key]: true }));
          setTimeout(() => setPriceUpdated(prev => ({ ...prev, [key]: false })), 1500);
        } catch (err) {
          console.error('Erreur AutoSave prix produit', err);
        }
      }, 700);
    });

    // Debounce: build price change proposals + fetch custom pricings after 1.5s of inactivity
    if (priceProposalTimer.current) window.clearTimeout(priceProposalTimer.current);
    priceProposalTimer.current = window.setTimeout(() => {
      if (!po?.items) return;
      const proposals: typeof priceProposals = [];
      Object.keys(prices).forEach((key) => {
        const item = (po.items || []).find((it: any) => it.__key === key);
        if (!item) return;
        const newPrice = Number(prices[key]);
        const oldPrice = Number(item.price ?? 0);
        if (newPrice !== oldPrice && !isNaN(newPrice) && oldPrice > 0 && newPrice > 0) {
          const productId = getProdId(item);
          if (!productId) return;
          const productName = item?.productId?.name || '';
          const pct = ((newPrice - oldPrice) / oldPrice) * 100;
          proposals.push({ key, productId, productName, oldPurchasePrice: oldPrice, newPurchasePrice: newPrice, pct });
        }
      });
      if (proposals.length > 0) {
        setPriceProposals(proposals);
        setShowPriceProposalBanner(true);
        // Fetch all custom pricings and filter by affected productIds
        // Increment generation to cancel any in-flight previous fetch
        const gen = ++pricingFetchGen.current;
        setLoadingCustomPricings(true);
        (async () => {
          try {
            const affectedProductIds = proposals.map((p) => p.productId);
            // Single request filtered by productIds — no pagination loop needed
            const res = await getAllPricings(affectedProductIds);
            if (pricingFetchGen.current !== gen) return; // stale, discard
            const allCustom: any[] = res?.data ?? [];
            const customProposals = allCustom
              .filter((cp: any) => affectedProductIds.includes(cp.productId))
              .map((cp: any) => {
                const proposal = proposals.find((p) => p.productId === cp.productId);
                if (!proposal) return null;
                const oldPrice = Number(cp.price ?? 0);
                const newPrice = Math.round((oldPrice * (1 + proposal.pct / 100)) * 100) / 100;
                return {
                  id: cp._id,
                  productId: cp.productId,
                  productName: cp.name || proposal.productName,
                  oldPrice,
                  newPrice,
                  pct: proposal.pct,
                  tags: cp.tags || [],
                  customerId: cp.customerId,
                };
              })
              .filter(Boolean);
            setCustomPricingProposals(customProposals);
          } catch (e) {
            console.error('Erreur chargement tarifs personnalisés', e);
          } finally {
            if (pricingFetchGen.current === gen) setLoadingCustomPricings(false);
          }
        })();
      } else {
        setShowPriceProposalBanner(false);
        setCustomPricingProposals([]);
      }
    }, 1500);

    return () => {
      if (priceProposalTimer.current) window.clearTimeout(priceProposalTimer.current);
      // Clear all per-product debounce timers still pending
      Object.values(productPriceTimers.current).forEach((t) => window.clearTimeout(t));
      productPriceTimers.current = {};
    };
  }, [prices]);

  // Cleanup on unmount: invalidate stale fetch generation + clear all lingering timers
  useEffect(() => {
    return () => {
      pricingFetchGen.current = -1; // any in-flight getAllPricings loop will abort
      if (priceProposalTimer.current) window.clearTimeout(priceProposalTimer.current);
      if (autoSaveTimer.current) window.clearTimeout(autoSaveTimer.current);
      Object.values(productPriceTimers.current).forEach((t) => window.clearTimeout(t));
      productPriceTimers.current = {};
    };
  }, []);

  // --- Save full reception (manual) ---
  const handleSaveReception = async () => {
    if (!po) return;
    // require date
    if (!receptionDate) {
      toast.error('Veuillez renseigner une date de réception avant de sauvegarder.');
      return;
    }
    setSavingReception(true);
    ignoreAutoSaveRef.current = true;
    try {
      const payload: any = {
        _id: po._id,
        receptionDate: new Date(receptionDate).toISOString(),
        tempReception: tempReception !== '' ? Number(tempReception) : undefined,
        items: buildItemsPayload(),
        deliveryFees: Number(deliveryFees ?? 0),
        feesDistributed: feesDistributed,
        totalHT: round2(totalHT),
        totalPrice: round2(totalTTC),
        blNumber,
        invoiceNumber,
      };
      const updated = await updatePurchase(payload);
      if (!updated || updated.error) {
        toast.error('Erreur mise à jour');
        return;
      }
      await syncProductsPrices();
      toast.success('Réception enregistrée');
      onUpdated();
      load();
    } finally {
      setSavingReception(false);
      ignoreAutoSaveRef.current = false;
      onClose();
    }
  };

  const handleMarkReceived = async () => {
    if (!po) return;
    if (po.status === 'received') return;
    if (!receptionDate) {
      toast.error('Veuillez renseigner une date de réception avant de marquer reçu.');
      return;
    }
  
    setMarkingReceived(true);
    ignoreAutoSaveRef.current = true;
    if (autoSaveTimer.current) {
      window.clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
  
    try {
      const id = po._id;
  
      // Prepare complete items payload with all details for accurate reception save
      const itemsPatch = buildItemsPayload();

      // Calculare total
      const itemsHT = itemsPatch.reduce((sum: any, it: any) => sum + Number(it.quantity) * Number(it.price), 0);
      const itemsVAT = itemsPatch.reduce((sum: any, it: any) => sum + (Number(it.quantity) * Number(it.price) * (Number(po.items?.[0]?.vat ?? 0) / 100)), 0);
      
      // Add delivery fees only if not distributed
      const fees = feesDistributed ? 0 : Number(deliveryFees ?? 0);
      const totalHT = round2(itemsHT + (Number.isFinite(fees) ? fees : 0));
      const totalPrice = round2(totalHT + itemsVAT);

      //  Save full reception BEFORE markReceived
      const upd = await updatePurchase({
        _id: id,
        id,
        receptionDate: new Date(receptionDate).toISOString(),
        items: itemsPatch,
        deliveryFees: Number(deliveryFees ?? 0),
        feesDistributed: feesDistributed,
        totalHT: round2(totalHT),
        totalPrice: round2(totalPrice),
        blNumber,
        invoiceNumber,
      });
      if (!upd || upd.error) {
        toast.error('Erreur lors de la sauvegarde des données de réception');
        return;
      }
  
      // Add reception details and mark as received
      const res = await markReceived(id);
      if (!res || res.error) {
        toast.error("Erreur lors du passage au statut 'reçu'");
        return;
      }
  
      await syncProductsPrices();
      toast.success('Marqué reçu');
      onUpdated();
      load();
    } finally {
      setMarkingReceived(false);
      ignoreAutoSaveRef.current = false;
      onClose();
    }
  };
  

  const handleMarkPartialReceived = async () => {
    if (!po) return;
    if (!receptionDate) {
      toast.error('Veuillez renseigner une date de réception avant de continuer.');
      return;
    }
    setMarkingPartial(true);
    ignoreAutoSaveRef.current = true;
    if (autoSaveTimer.current) {
      window.clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
    try {
      const id = po._id;
      // First save the items (lot, dlc, area, prices etc.)
      const itemsPatch = buildItemsPayload();
      const itemsHT = itemsPatch.reduce((sum: number, it: any) => sum + Number(it.quantity) * Number(it.price), 0);
      const itemsVAT = itemsPatch.reduce((sum: number, it: any) => sum + (Number(it.quantity) * Number(it.price) * (Number(it.vat ?? 0) / 100)), 0);
      const fees = feesDistributed ? 0 : Number(deliveryFees ?? 0);
      const calcTotalHT = round2(itemsHT + (Number.isFinite(fees) ? fees : 0));
      const calcTotalPrice = round2(calcTotalHT + itemsVAT);
      const upd = await updatePurchase({
        _id: id,
        receptionDate: new Date(receptionDate).toISOString(),
        items: itemsPatch,
        deliveryFees: Number(deliveryFees ?? 0),
        feesDistributed,
        totalHT: calcTotalHT,
        totalPrice: calcTotalPrice,
        blNumber,
        invoiceNumber,
      });
      if (!upd || upd.error) {
        toast.error('Erreur lors de la sauvegarde des données de réception');
        return;
      }
      // Build received items payload (carry the sub-document id so the backend
      // matches by id instead of position — robust against re-ordering).
      const receivedItemsPayload = (po.items || []).map((it: any) => ({
        itemId: it._id,
        receivedQty: Number(receivedQuantities[it.__key] ?? 0),
      }));
      const res = await markPartialReceived(id, receivedItemsPayload, new Date(receptionDate).toISOString());
      if (!res || res.error) {
        toast.error("Erreur lors de la réception partielle");
        return;
      }
      await syncProductsPrices();
      const allReceived = res.data?.status === 'received';
      toast.success(allReceived ? 'Totalité réceptionnée !' : 'Réception partielle enregistrée');
      onUpdated();
      load();
    } finally {
      setMarkingPartial(false);
      ignoreAutoSaveRef.current = false;
      onClose();
    }
  };

  //  Cancel a received / partially received reception: the backend pulls every
  //  received quantity back out of stock (on its original lot+area) and returns
  //  the purchase to 'pending'. Auto-save is paused to avoid racing the request.
  const handleCancelReception = async () => {
    if (!po) return;
    setCancellingReception(true);
    ignoreAutoSaveRef.current = true;
    if (autoSaveTimer.current) {
      window.clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
    try {
      const res = await cancelPurchaseReception(po._id);
      if (!res || res.error) {
        // errorHandler already surfaced the precise backend message (permission, etc.)
        return;
      }
      toast.success('Réception annulée — les stocks ont été restaurés');
      onCancelClose();
      onUpdated();
      onClose();
    } finally {
      setCancellingReception(false);
      ignoreAutoSaveRef.current = false;
    }
  };

  const handleMarkPaid = async () => {
    if (!po || po.pay) return;
    setMarkingPaid(true);
    try {
      // Compute remaining amount to pay and add a final settlement payment if needed
      const totalPaid = payments.reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
      const remaining = Math.round((totalTTC - totalPaid) * 100) / 100;
      const finalPayments = remaining > 0
        ? [...payments, { amount: remaining, date: new Date().toISOString(), method: newPayMethod || 'virement', note: 'Solde' }]
        : payments;
      const updated = await updatePurchase({
        _id: po._id,
        pay: true,
        payments: finalPayments,
      });
      if (updated && !updated.error) {
        toast.success('Commande soldée et marquée payée');
        onUpdated();
        load();
      } else toast.error('Erreur paiement');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleAddPayment = async () => {
    const amount = parseFloat(newPayAmount);
    if (!po || isNaN(amount) || amount <= 0) {
      toast.error('Montant invalide');
      return;
    }
    setSavingPayment(true);
    try {
      const newPayment = {
        amount,
        date: new Date(newPayDate).toISOString(),
        method: newPayMethod,
        note: newPayNote.trim(),
      };
      const updatedPayments = [...payments, newPayment];
      const updated = await updatePurchase({
        _id: po._id,
        payments: updatedPayments,
      });
      if (updated && !updated.error) {
        setPayments(updatedPayments);
        setNewPayAmount('');
        setNewPayNote('');
        setNewPayDate(new Date().toISOString().slice(0, 10));
        setShowPayForm(false);
        toast.success('Paiement ajouté');
        onUpdated();
      } else toast.error('Erreur lors de l\'ajout du paiement');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleRemovePayment = async (index: number) => {
    if (!po) return;
    const updatedPayments = payments.filter((_: any, i: number) => i !== index);
    // If the PO was fully paid, removing a payment resets pay to false
    const wasFullyPaid = po.pay === true;
    const updated = await updatePurchase({
      _id: po._id,
      payments: updatedPayments,
      ...(wasFullyPaid ? { pay: false } : {}),
    });
    if (updated && !updated.error) {
      setPayments(updatedPayments);
      onUpdated();
      // Reload to reflect pay status change in po
      load();
    } else toast.error('Erreur suppression paiement');
  };

  const handleDeletePurchase = async () => {
    if (!po) return;
    try {
      const res = await deletePurchase(po._id);
      if (res && !res.error) {
        toast.success('Commande d\'achat supprimée avec succès');
        onDeleteClose();
        onClose();
        // Trigger parent refresh
        onUpdated();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // --- PDF Invoice ---
  const handleUploadInvoicePdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !po) return;
    setPdfUploading(true);
    try {
      const res = await uploadPurchaseInvoice(po._id, file);
      if (!res || res.error) { toast.error('Erreur lors de l\'upload du PDF'); return; }
      toast.success('Facture PDF uploadée avec succès');
      setPo((prev: any) => ({ ...prev, pdfPurchase: res.key }));
    } finally {
      setPdfUploading(false);
      if (pdfInputRef.current) pdfInputRef.current.value = '';
    }
  };

  const handleOpenInvoicePdf = async () => {
    if (!po?._id) return;
    try {
      const url = await getPurchaseInvoiceUrl(po._id);
      if (!url) { toast.error('Aucune facture PDF disponible'); return; }
      setPdfUrl(url);
      setPdfViewerOpen(true);
    } catch {
      toast.error('Impossible de charger le PDF');
    }
  };

  // --- Persist items immediately (after structural add/remove) ---
  const persistItemsImmediately = async (
    newItems: any[],
    nQ: {[key:string]: number},
    nP: {[key:string]: number},
    nL: {[key:string]: string},
    nA: {[key:string]: string},
    nD: {[key:string]: string},
    nT: {[key:string]: string}
  ) => {
    try {
      setAutoSaving(true);
      setAutoSaveError(null);
      const itemsPatch = newItems.map((item: any) => {
        const k = item.__key;
        const quantity = Number(nQ[k] ?? item.quantity ?? 0);
        const price = Number(nP[k] ?? item.price ?? 0);
        const temp = nT[k];
        return {
          ...item,
          productId: getProdId(item),
          quantity,
          price,
          lotNumber: nL[k] ?? item.lotNumber ?? '',
          area: nA[k] ?? item.area ?? 'zone-a',
          dlc: isItemNoDlc(item) ? '' : (nD[k] ? new Date(nD[k]).toISOString() : (item.dlc ? new Date(item.dlc).toISOString() : '')),
          receptionTemp: temp !== '' ? Number(temp) : undefined,
        };
      });
      const itemsHT = itemsPatch.reduce((sum: number, it: any) => sum + (Number(it.quantity) * Number(it.price)), 0);
      const totalVAT = itemsPatch.reduce((sum: number, it: any) => sum + ((Number(it.quantity) * Number(it.price)) * (Number(it.vat ?? it.vatPurchase ?? 0) / 100)), 0);
      const fees = feesDistributed ? 0 : Number(deliveryFees ?? 0);
      const calcTotalHT = round2(itemsHT + (Number.isFinite(fees) ? fees : 0));
      const calcTotalPrice = round2(calcTotalHT + totalVAT);
      const updated = await updatePurchase({
        _id: po._id,
        items: itemsPatch,
        deliveryFees: Number(deliveryFees ?? 0),
        feesDistributed,
        totalHT: calcTotalHT,
        totalPrice: calcTotalPrice,
      });
      if (!updated || updated.error) throw new Error('update failed');
      // Reconcile freshly created sub-document _ids back into local state so
      // subsequent saves match by id. The backend rebuilds items in the exact
      // order of the payload we just sent (== prev.items), so position is a
      // reliable mapping here. __key is preserved by the spread, so the local
      // state maps stay valid (only the missing _id gets filled in).
      const serverItems = (updated as any)?.data?.items;
      if (Array.isArray(serverItems)) {
        setPo((prev: any) => {
          if (!prev?.items) return prev;
          const merged = prev.items.map((it: any, idx: number) =>
            it._id ? it : { ...it, _id: serverItems[idx]?._id },
          );
          return { ...prev, items: merged };
        });
      }
      setLastSavedAt(Date.now());
      onUpdated?.();
    } catch (e) {
      setAutoSaveError('Erreur de sauvegarde');
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setAutoSaving(false);
    }
  };

  // --- Remove an item ---
  // Keyed by __key: no index re-mapping needed, so removing a middle line can
  // never shift another line's data. We just drop the line and its map entries.
  const removeItem = async (keyToRemove: string) => {
    const newItems = (po.items || []).filter((it: any) => it.__key !== keyToRemove);
    const omitKey = <T,>(map: {[key:string]: T}): {[key:string]: T} => {
      if (!(keyToRemove in map)) return map;
      const { [keyToRemove]: _removed, ...rest } = map;
      return rest;
    };
    const nQ = omitKey(quantities);
    const nP = omitKey(prices);
    const nV = omitKey(vats);
    const nL = omitKey(lotNumbers);
    const nD = omitKey(dlcs);
    const nA = omitKey(areas);
    const nT = omitKey(receptionTemps);
    const nRQ = omitKey(receivedQuantities);
    const nPU = omitKey(priceUpdated);
    setPo((prev: any) => ({ ...prev, items: newItems }));
    setQuantities(nQ);
    setPrices(nP);
    setVats(nV);
    setLotNumbers(nL);
    setDlcs(nD);
    setAreas(nA);
    setReceptionTemps(nT);
    setReceivedQuantities(nRQ);
    setPriceUpdated(nPU);
    await persistItemsImmediately(newItems, nQ, nP, nL, nA, nD, nT);
  };

  // --- Add a new item ---
  const handleAddItem = async () => {
    if (!newProductId) { toast.error('Sélectionnez un produit'); return; }
    const prod = allProducts.find((p: any) => (p._id || p.id) === newProductId);
    if (!prod) return;
    const newKey = genRowKey();
    const newItem = {
      __key: newKey,
      productId: prod,
      quantity: newProductQty,
      price: newProductPrice,
      lotNumber: '',
      area: 'zone-a',
      dlc: '',
      vat: prod.vatPurchase ?? prod.vat ?? 0,
    };
    const newItems = [...(po.items || []), newItem];
    const nQ = { ...quantities, [newKey]: newProductQty };
    const nP = { ...prices, [newKey]: newProductPrice };
    const nV = { ...vats, [newKey]: Number(prod.vatPurchase ?? prod.vat ?? 0) };
    const nL = { ...lotNumbers, [newKey]: '' };
    const nD = { ...dlcs, [newKey]: '' };
    const nA = { ...areas, [newKey]: 'zone-a' };
    const nT = { ...receptionTemps, [newKey]: '' };
    setPo((prev: any) => ({ ...prev, items: newItems }));
    setQuantities(nQ);
    setPrices(nP);
    setVats(nV);
    setLotNumbers(nL);
    setDlcs(nD);
    setAreas(nA);
    setReceptionTemps(nT);
    setReceivedQuantities(prev => ({ ...prev, [newKey]: newProductQty }));
    setPriceUpdated(prev => ({ ...prev, [newKey]: false }));
    setNewProductId('');
    setNewProductQty(1);
    setNewProductPrice(0);
    setShowAddRow(false);
    await persistItemsImmediately(newItems, nQ, nP, nL, nA, nD, nT);
    toast.success('Article ajouté');
  };

  // --- Export XLSX ---
  const handleExportXlsx = () => {
    if (!po) return;
    const rows = (po.items || []).map((it: any) => ({
      'Numéro de commande': po.purchaseId || '',
      'Référence client': '',
      'Type de process': '',
      'Fournisseur': supplierName,
      'Date de réception prévue': deliveryPlanned
        ? new Date(deliveryPlanned).toLocaleDateString('fr-FR')
        : '',
      'Nbr de palettes attendu': '',
      'Nbr de colis attendu': '',
      'SKU produit': it?.productId?.ref || '',
      'Quantité produit': Number(quantities[it.__key] ?? it.quantity ?? 0),
      'Poids total': '',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Commande');
    XLSX.writeFile(wb, `BC_${po.purchaseId || po._id}.xlsx`);
  };

  // Permissions: owner (no ownerId in localStorage) OR 'all' OR 'purchases:override'
  const canOverride = useMemo(() => {
    const storedPermissions = JSON.parse(localStorage.getItem('permissions') || '[]') as string[];
    const isOwner = !localStorage.getItem('ownerId');
    return isOwner || storedPermissions.includes('all') || storedPermissions.includes('purchases:override');
  }, []);

  // disable editing when already received or fully paid, unless user can override
  const isLocked = (po?.status === 'received' || po?.pay === true) && !canOverride;
  // Partial purchases cannot be deleted (stock already moved)
  const canDelete = po?.status === 'pending' || (canOverride && po?.status === 'received');

  // Reverse a received/partial reception (restores stock, reverts to 'pending').
  // Mirrors the backend gate: a closed order (received OR paid) needs override,
  // while an unpaid partial reception can be undone by any 'purchases' user.
  const canCancelReception =
    (po?.status === 'received' || po?.status === 'partial') &&
    (canOverride || (po?.status === 'partial' && po?.pay !== true));

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

        <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto">
          <Card extra="p-6 border border-gray-200 rounded-2xl shadow-lg bg-white dark:bg-navy-800">
            {loading && (
              <div className="flex justify-center items-center py-10">
                <div className="animate-spin h-6 w-6 border-b-2 border-brand-500 rounded-full mr-2" />
                <p className="text-sm text-gray-500">Chargement…</p>
              </div>
            )}

            {!loading && po && (
            <>
              {/* Header */}
              <div className="flex justify-between items-start mb-6 border-b border-gray-200 pb-4">
                <div>
                  <h2 className="text-xl font-semibold text-navy-700">
                    Bon de commande <span className="text-[#0260CB]">{po.purchaseId}</span>
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Fournisseur : <span className="font-medium text-gray-900">{supplierName}</span>
                  </p>
                  {po.createdBy && (
                    <p className="text-xs text-gray-400 mt-1">
                      Créé par : {typeof po.createdBy === 'string' ? po.createdBy : po.createdBy?.fullName || po.createdBy?.email || '—'}
                    </p>
                  )}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      po.status === 'received' ? 'bg-green-100 text-green-700' :
                      po.status === 'partial' ? 'bg-amber-100 text-amber-800' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {po.status === 'received' ? '✓ Reçu' : po.status === 'partial' ? 'Réception partielle' : 'En cours'}
                    </span>
                    {po.pay ? (
                     <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-200 text-emerald-900">
                       ✓ Payé définitivement
                     </span>
                   ) : payments.length > 0 ? (
                     <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                       Paiement partiel ({payments.reduce((s: number, p: any) => s + Number(p.amount), 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })})
                     </span>
                   ) : (
                     <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                       En attente de paiement
                     </span>
                   )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <SaveStatus
                    status={
                      (po.status === 'received' || po.status === 'partial')
                        ? 'saved' // lock visual state when received or partial
                        : autoSaving
                          ? 'saving'
                          : autoSaveError
                            ? 'error'
                            : lastSavedAt
                              ? 'saved'
                              : 'idle'
                    }
                  />
                  <button
                    onClick={handleExportXlsx}
                    title="Exporter en XLSX"
                    className="px-3 py-1 rounded-md border text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                  >
                    <MdDownload className="w-4 h-4" />
                    Export XLSX
                  </button>
                  <button
                   onClick={handleOpenMagicLink}
                   disabled={!magicToken}
                   title="Voir la page fournisseur"
                   className="px-3 py-1 rounded-md border text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50"
                 >
                   <MdOpenInNew className="w-4 h-4" />
                   Lien fournisseur
                 </button>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <MdClose className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Infos */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Date commande</label>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {po.purchaseDate ? new Date(po.purchaseDate).toLocaleDateString('fr-FR') : po.createdAt ? new Date(po.createdAt).toLocaleDateString('fr-FR') : '—'}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Livraison prévue</label>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {deliveryPlanned ? new Date(deliveryPlanned).toLocaleDateString('fr-FR') : '—'}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Réception (date) *</label>
                  <input
                    type="date"
                    value={receptionDate}
                    onChange={(e) => setReceptionDate(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-brand-500 outline-none"
                    disabled={isLocked}
                  />
                  {po.status === 'partial' && po.receptionDate && (
                    <p className="mt-1 text-[11px] text-amber-600">
                      Réception partielle le {new Date(po.receptionDate).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Température réception (°C)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={tempReception}
                    onChange={(e) => setTempReception(e.target.value)}
                    placeholder="Ex: 3.2"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-brand-500 outline-none"
                    disabled={isLocked}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">N° BL (Bon de livraison)</label>
                  <input
                    type="text"
                    value={blNumber}
                    onChange={(e) => setBlNumber(e.target.value)}
                    placeholder="Ex: BL-2026-001"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-brand-500 outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">N° Facture fournisseur</label>
                  <input
                    type="text"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="Ex: FAC-2026-001"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-brand-500 outline-none"
                  />
                </div>

                {/* PDF Invoice */}
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Facture PDF fournisseur</label>
                  <div className="mt-1 flex items-center gap-2">
                    {po.pdfPurchase ? (
                      <button
                        onClick={handleOpenInvoicePdf}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 text-xs font-medium transition-colors"
                      >
                        <MdPictureAsPdf className="w-4 h-4" />
                        Voir la facture
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Aucune facture</span>
                    )}
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={handleUploadInvoicePdf}
                    />
                    <button
                      onClick={() => pdfInputRef.current?.click()}
                      disabled={pdfUploading}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 text-xs transition-colors disabled:opacity-50"
                    >
                      <MdUpload className="w-4 h-4" />
                      {pdfUploading ? 'Upload...' : po.pdfPurchase ? 'Remplacer' : 'Ajouter'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Articles */}
              <div className="mb-8">
                <h3 className="text-base font-semibold text-navy-700 mb-3">Articles</h3>

                {/* Responsive card list on small screens, table on larger */}
                <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                  <table className="w-full min-w-[700px] text-xs text-gray-700 border-collapse">
                    <colgroup>
                      <col style={{ minWidth: '140px' }} />  {/* Produit */}
                      <col style={{ width: '90px' }} />       {/* Qté */}
                      <col style={{ width: '96px' }} />       {/* P.U. */}
                      <col style={{ width: '78px' }} />       {/* TVA */}
                      <col style={{ width: '80px' }} />       {/* Total */}
                      <col style={{ width: '90px' }} />      {/* N° lot */}
                      <col style={{ width: '90px' }} />      {/* DLC */}
                      <col style={{ width: '82px' }} />       {/* Zone */}
                      <col style={{ width: '68px' }} />       {/* Temp */}
                      {(po.status === 'pending' || po.status === 'partial') && !isLocked && <col style={{ width: '90px' }} />}
                      {!isLocked && <col style={{ width: '36px' }} />}
                    </colgroup>
                    <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                      <tr>
                        <th className="text-left py-2 px-3 font-semibold whitespace-nowrap">Produit</th>
                        <th className="text-right py-2 px-2 font-semibold whitespace-nowrap">Qté</th>
                        <th className="text-right py-2 px-2 font-semibold whitespace-nowrap">P.U. (€)</th>
                        <th className="text-right py-2 px-2 font-semibold whitespace-nowrap">TVA%</th>
                        <th className="text-right py-2 px-2 font-semibold whitespace-nowrap">Total HT</th>
                        <th className="text-left py-2 px-2 font-semibold whitespace-nowrap">N° Lot</th>
                        <th className="text-left py-2 px-2 font-semibold whitespace-nowrap">DLC</th>
                        <th className="text-left py-2 px-2 font-semibold whitespace-nowrap">Zone</th>
                        <th className="text-left py-2 px-2 font-semibold whitespace-nowrap">°C</th>
                        {(po.status === 'pending' || po.status === 'partial') && !isLocked && (
                          <th className="text-right py-2 px-2 font-semibold whitespace-nowrap bg-blue-50 text-blue-700">Récep. partielle</th>
                        )}
                        {!isLocked && <th className="py-2 px-2" />}
                      </tr>
                    </thead>
                    <tbody>
                      {(po.items || []).map((it: any, i: number) => {
                        const rowKey = it.__key || `item-${i}`;
                        const q = Number(quantities[rowKey] ?? it.quantity);
                        const pu = Number(prices[rowKey] ?? it.price);
                        const inputCls = "w-full text-right rounded border border-gray-200 bg-white px-1.5 py-1 text-xs focus:border-brand-500 focus:ring-0 outline-none disabled:bg-gray-50 disabled:text-gray-400";
                        return (
                          <tr key={rowKey} className="border-t border-gray-100 hover:bg-gray-50 align-middle">
                            {/* Produit */}
                            <td className="py-2 px-3 font-medium text-gray-800">
                              <div className="leading-tight">
                                <span className="block truncate max-w-[180px]" title={it?.productId?.name}>{it?.productId?.name}</span>
                                {it?.productId?.ref && <span className="text-gray-400 font-normal">{it.productId.ref}</span>}
                              </div>
                            </td>

                            {/* Qté */}
                            <td className="py-2 px-2">
                              <input
                                type="number"
                                min={0}
                                value={isNaN(q) ? 0 : q}
                                onChange={(e) => setQuantities(prev => ({ ...prev, [rowKey]: Number(e.target.value) }))}
                                className={inputCls + ' min-w-[68px]'}
                                disabled={isLocked}
                              />
                            </td>

                            {/* P.U. */}
                            <td className="py-2 px-2">
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                value={isNaN(pu) ? 0 : pu}
                                onChange={(e) => setPrices(prev => ({ ...prev, [rowKey]: Number(e.target.value) }))}
                                className={inputCls}
                                disabled={isLocked}
                              />
                              {priceUpdated[rowKey] && (
                                <span className="block mt-0.5 text-[10px] text-emerald-600 text-right leading-tight">✓ prix maj</span>
                              )}
                            </td>

                            {/* TVA */}
                            <td className="py-2 px-2">
                              <input
                                type="number"
                                min={0}
                                step="0.5"
                                value={vats[rowKey] ?? getVatRate(it)}
                                onChange={(e) => setVats(prev => ({ ...prev, [rowKey]: Number(e.target.value) }))}
                                className={inputCls + ' min-w-[56px]'}
                                disabled={isLocked}
                              />
                            </td>

                            {/* Total HT */}
                            <td className="py-2 px-2 text-right font-medium text-gray-700 whitespace-nowrap">
                              {formatMoney((isNaN(pu) ? 0 : pu) * (isNaN(q) ? 0 : q))}
                            </td>

                            {/* N° lot */}
                            <td className="py-2 px-2">
                              <input
                                type="text"
                                value={lotNumbers[rowKey] || ''}
                                onChange={(e) => setLotNumbers(prev => ({ ...prev, [rowKey]: e.target.value }))}
                                placeholder="Lot…"
                                className="w-full rounded border border-gray-200 bg-white px-1.5 py-1 text-xs focus:border-brand-500 outline-none disabled:bg-gray-50 disabled:text-gray-400"
                                disabled={isLocked}
                              />
                            </td>

                            {/* DLC */}
                            <td className="py-2 px-2">
                              {isItemNoDlc(it) ? (
                                <span className="text-[11px] text-gray-400 italic whitespace-nowrap">Sans DLC</span>
                              ) : (
                                <input
                                  type="date"
                                  value={dlcs[rowKey] || ''}
                                  onChange={(e) => setDlcs(prev => ({ ...prev, [rowKey]: e.target.value }))}
                                  className="w-full rounded border border-gray-200 bg-white px-1.5 py-1 text-xs focus:border-brand-500 outline-none disabled:bg-gray-50 disabled:text-gray-400"
                                  disabled={isLocked}
                                />
                              )}
                            </td>

                            {/* Zone */}
                            <td className="py-2 px-2">
                              <select
                                className="w-full rounded border border-gray-200 bg-white px-1.5 py-1 text-xs focus:border-brand-500 outline-none disabled:bg-gray-50 disabled:text-gray-400"
                                value={areas[rowKey] || 'zone-a'}
                                onChange={(e) => setAreas(prev => ({ ...prev, [rowKey]: e.target.value }))}
                                disabled={isLocked}
                              >
                                <option value="zone-a">Zone A</option>
                                <option value="zone-b">Zone B</option>
                                <option value="zone-c">Zone C</option>
                              </select>
                            </td>

                            {/* Temp */}
                            <td className="py-2 px-2">
                              <input
                                type="number"
                                step="0.1"
                                value={receptionTemps[rowKey] || ''}
                                onChange={(e) => setReceptionTemps(prev => ({ ...prev, [rowKey]: e.target.value }))}
                                placeholder="—"
                                className={inputCls}
                                disabled={isLocked}
                              />
                            </td>

                            {/* Qté à réceptionner */}
                            {(po.status === 'pending' || po.status === 'partial') && !isLocked && (
                              <td className="py-2 px-2 bg-blue-50/40">
                                <input
                                  type="number"
                                  min={0}
                                  max={Number(quantities[rowKey] ?? it.quantity)}
                                  step={1}
                                  value={receivedQuantities[rowKey] ?? Math.max(0, Number(quantities[rowKey] ?? it.quantity) - Number(it.receivedQuantity ?? 0))}
                                  onChange={(e) => setReceivedQuantities(prev => ({ ...prev, [rowKey]: Number(e.target.value) }))}
                                  className="w-full text-right rounded border border-blue-300 bg-blue-50 px-1.5 py-1 text-xs focus:border-blue-500 outline-none font-medium"
                                />
                                {Number(it.receivedQuantity ?? 0) > 0 && (
                                  <span className="block mt-0.5 text-[10px] text-emerald-600 text-right leading-tight">{Number(it.receivedQuantity)} reçu</span>
                                )}
                              </td>
                            )}

                            {/* Supprimer */}
                            {!isLocked && (
                              <td className="py-2 px-1 text-center">
                                <button
                                  onClick={() => removeItem(rowKey)}
                                  className="text-red-300 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
                                  title="Supprimer cet article"
                                >
                                  <MdDelete className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                      {(!po.items || po.items.length === 0) && (
                        <tr>
                          <td colSpan={isLocked ? 9 : ((po.status === 'pending' || po.status === 'partial') ? 11 : 10)} className="py-6 text-center text-gray-400 italic text-xs">Aucun article</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile card view */}
                <div className="sm:hidden flex flex-col gap-3">
                  {(po.items || []).length === 0 && (
                    <p className="text-center text-gray-400 italic text-xs py-4">Aucun article</p>
                  )}
                  {(po.items || []).map((it: any, i: number) => {
                    const rowKey = it.__key || `item-${i}`;
                    const q = Number(quantities[rowKey] ?? it.quantity);
                    const pu = Number(prices[rowKey] ?? it.price);
                    const fieldCls = "w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-xs focus:border-brand-500 outline-none disabled:bg-gray-50";
                    return (
                      <div key={rowKey} className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-gray-800 text-xs">{it?.productId?.name}</p>
                            {it?.productId?.ref && <p className="text-[11px] text-gray-400">{it.productId.ref}</p>}
                          </div>
                          {!isLocked && (
                            <button onClick={() => removeItem(rowKey)} className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50">
                              <MdDelete className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5">Qté</label>
                            <input type="number" min={0} value={isNaN(q) ? 0 : q} onChange={(e) => setQuantities(prev => ({ ...prev, [rowKey]: Number(e.target.value) }))} className={fieldCls} disabled={isLocked} />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5">P.U. (€)</label>
                            <input type="number" min={0} step="0.01" value={isNaN(pu) ? 0 : pu} onChange={(e) => setPrices(prev => ({ ...prev, [rowKey]: Number(e.target.value) }))} className={fieldCls} disabled={isLocked} />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5">TVA (%)</label>
                            <input type="number" min={0} step="0.5" value={vats[rowKey] ?? getVatRate(it)} onChange={(e) => setVats(prev => ({ ...prev, [rowKey]: Number(e.target.value) }))} className={fieldCls} disabled={isLocked} />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5">Total HT</label>
                            <p className="text-xs font-semibold text-gray-700 py-1.5">{formatMoney((isNaN(pu) ? 0 : pu) * (isNaN(q) ? 0 : q))}</p>
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5">N° Lot</label>
                            <input type="text" value={lotNumbers[rowKey] || ''} onChange={(e) => setLotNumbers(prev => ({ ...prev, [rowKey]: e.target.value }))} placeholder="Lot…" className={fieldCls} disabled={isLocked} />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5">DLC</label>
                            {isItemNoDlc(it) ? <span className="text-xs text-gray-400 italic">Sans DLC</span> : (
                              <input type="date" value={dlcs[rowKey] || ''} onChange={(e) => setDlcs(prev => ({ ...prev, [rowKey]: e.target.value }))} className={fieldCls} disabled={isLocked} />
                            )}
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5">Zone</label>
                            <select value={areas[rowKey] || 'zone-a'} onChange={(e) => setAreas(prev => ({ ...prev, [rowKey]: e.target.value }))} className={fieldCls} disabled={isLocked}>
                              <option value="zone-a">Zone A</option>
                              <option value="zone-b">Zone B</option>
                              <option value="zone-c">Zone C</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5">Temp. (°C)</label>
                            <input type="number" step="0.1" value={receptionTemps[rowKey] || ''} onChange={(e) => setReceptionTemps(prev => ({ ...prev, [rowKey]: e.target.value }))} placeholder="—" className={fieldCls} disabled={isLocked} />
                          </div>
                          {(po.status === 'pending' || po.status === 'partial') && !isLocked && (
                            <div className="col-span-2">
                              <label className="text-[10px] font-medium text-blue-600 uppercase block mb-0.5">Qté à réceptionner</label>
                              <input type="number" min={0} max={Number(quantities[rowKey] ?? it.quantity)} step={1} value={receivedQuantities[rowKey] ?? Math.max(0, Number(quantities[rowKey] ?? it.quantity) - Number(it.receivedQuantity ?? 0))} onChange={(e) => setReceivedQuantities(prev => ({ ...prev, [rowKey]: Number(e.target.value) }))} className="w-full rounded border border-blue-300 bg-blue-50 px-2 py-1.5 text-xs focus:border-blue-500 outline-none font-medium" />
                              {Number(it.receivedQuantity ?? 0) > 0 && <span className="text-[10px] text-emerald-600">{Number(it.receivedQuantity)} déjà reçu</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {!isLocked && (
                  <div className="mt-3">
                    {!showAddRow ? (
                      <button
                        onClick={() => setShowAddRow(true)}
                        className="flex items-center gap-1 text-xs text-[#0260CB] hover:text-[#01499b] px-3 py-1.5 rounded border border-dashed border-[#0260CB] hover:bg-blue-50"
                      >
                        <MdAdd className="w-4 h-4" />
                        Ajouter un article
                      </button>
                    ) : (
                      <div className="flex flex-wrap items-end gap-2 mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex-1 min-w-[200px] relative" ref={productSearchRef}>
                          <label className="text-xs text-gray-500 block mb-1">Produit</label>
                          {/* Searchable product picker */}
                          <div className="relative">
                            <input
                              type="text"
                              value={productSearch}
                              onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                              onFocus={() => setShowProductDropdown(true)}
                              placeholder={newProductId
                                ? (allProducts.find((p: any) => (p._id || p.id) === newProductId)?.name || 'Produit sélectionné')
                                : 'Rechercher un produit…'
                              }
                              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-brand-500 outline-none bg-white pr-7"
                              autoComplete="off"
                            />
                            {newProductId && (
                              <button
                                type="button"
                                onClick={() => { setNewProductId(''); setProductSearch(''); setNewProductPrice(0); }}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                title="Effacer"
                              >
                                <MdClose className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          {showProductDropdown && (
                            <div
                              className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto text-xs"
                              onMouseDown={(e) => e.preventDefault()} // keep focus on input
                            >
                              {allProducts
                                .filter((p: any) => {
                                  const pid = p._id || p.id;
                                  const alreadyAdded = (po.items || []).some((it: any) => getProdId(it) === pid);
                                  if (alreadyAdded) return false;
                                  if (!productSearch.trim()) return true;
                                  const q = productSearch.toLowerCase();
                                  return (
                                    (p.name || '').toLowerCase().includes(q) ||
                                    (p.ref || '').toLowerCase().includes(q)
                                  );
                                })
                                .map((p: any) => {
                                  const pid = p._id || p.id;
                                  const pname = p.name || p.displayName || '(sans nom)';
                                  return (
                                    <button
                                      key={pid}
                                      type="button"
                                      onClick={() => {
                                        setNewProductId(pid);
                                        setProductSearch('');
                                        setNewProductPrice(Number(p.purchasePrice ?? p.unitPrice ?? 0));
                                        setShowProductDropdown(false);
                                      }}
                                      className={`w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between gap-2 ${
                                        pid === newProductId ? 'bg-blue-50 font-semibold text-brand-600' : 'text-gray-700'
                                      }`}
                                    >
                                      <span className="truncate">{pname}</span>
                                      {p.ref && <span className="shrink-0 text-gray-400">{p.ref}</span>}
                                    </button>
                                  );
                                })}
                              {allProducts.filter((p: any) => {
                                const pid = p._id || p.id;
                                if ((po.items || []).some((it: any) => getProdId(it) === pid)) return false;
                                if (!productSearch.trim()) return true;
                                const q = productSearch.toLowerCase();
                                return (p.name || '').toLowerCase().includes(q) || (p.ref || '').toLowerCase().includes(q);
                              }).length === 0 && (
                                <p className="px-3 py-2 text-gray-400 italic">Aucun produit trouvé</p>
                              )}
                            </div>
                          )}
                          {/* Close dropdown on outside click */}
                          {showProductDropdown && (
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setShowProductDropdown(false)}
                            />
                          )}
                          {newProductId && (
                            <p className="mt-1 text-[11px] text-brand-600 font-medium">
                              ✓ {allProducts.find((p: any) => (p._id || p.id) === newProductId)?.name}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Quantité</label>
                          <input
                            type="number"
                            min={1}
                            value={newProductQty}
                            onChange={(e) => setNewProductQty(Number(e.target.value))}
                            className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-brand-500 outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Prix unitaire (€)</label>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={newProductPrice}
                            onChange={(e) => setNewProductPrice(Number(e.target.value))}
                            className="w-28 rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-brand-500 outline-none"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleAddItem}
                            className="px-3 py-1.5 bg-[#0260CB] text-white rounded-md text-xs hover:bg-[#01499b]"
                          >
                            Ajouter
                          </button>
                          <button
                            onClick={() => { setShowAddRow(false); setNewProductId(''); setNewProductQty(1); setNewProductPrice(0); setProductSearch(''); setShowProductDropdown(false); }}
                            className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-md text-xs hover:bg-gray-200"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-4">
                <div className="text-sm text-navy-700 flex flex-col items-end gap-0.5">
                  <div className="flex gap-4"><span className="text-gray-500">Sous-total articles (HT)</span><span className="font-medium">{formatMoney(totalArticlesHT)}</span></div>
                  <div className="flex gap-4 items-center">
                    <span className="text-gray-500">Frais de livraison</span>
                    {feesDistributed ? (
                      <div className="flex items-center gap-2">
                        <span className="w-28 text-right text-xs font-medium text-gray-700 px-2 py-1 bg-gray-50 border border-gray-200 rounded-md">
                          {formatMoney(deliveryFees)}
                        </span>
                        <span className="text-[11px] text-emerald-600 font-medium whitespace-nowrap">
                          ✓ Répartis sur les produits
                        </span>
                      </div>
                    ) : (
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={isNaN(deliveryFees) ? 0 : deliveryFees}
                        onChange={(e) => setDeliveryFees(Number(e.target.value))}
                        className="w-28 text-right rounded-md border border-gray-300 px-2 py-1 text-xs font-medium focus:border-brand-500 outline-none"
                        disabled={isLocked}
                      />
                    )}
                  </div>
                  
                  {/* Option fees repartition */}
                  {deliveryFees > 0 && !isLocked && !feesDistributed && (
                    <div className="flex gap-2 items-center mt-2 px-3 py-2 bg-blue-50 rounded-md border border-blue-200">
                      <input
                        type="checkbox"
                        id="distributeFeesToProducts"
                        checked={distributeFeesToProducts}
                        onChange={(e) => setDistributeFeesToProducts(e.target.checked)}
                        className="w-4 h-4 text-[#0260CB] border-gray-300 rounded focus:ring-[#0260CB]"
                      />
                      <label htmlFor="distributeFeesToProducts" className="text-xs text-gray-700 cursor-pointer">
                        Répartir les frais sur les produits (proportionnellement aux quantités)
                      </label>
                      {distributeFeesToProducts && (
                        <button
                          onClick={handleDistributeFeesToProducts}
                          className="ml-2 px-3 py-1 bg-[#0260CB] text-white rounded-md text-xs hover:bg-[#01499b]"
                        >
                          Appliquer
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Message information on fees */}
                  {feesDistributed && deliveryFees > 0 && (
                    <div className="flex gap-2 items-start mt-2 px-3 py-2 bg-emerald-50 rounded-md border border-emerald-200">
                      <svg className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs text-emerald-800">
                        Les frais de livraison ({formatMoney(deliveryFees)}) ont été répartis sur les prix unitaires des produits proportionnellement aux quantités commandées.
                      </span>
                    </div>
                  )}

                  <div className="flex gap-4"><span className="text-gray-500">TVA totale</span><span className="font-medium">{formatMoney(totalTVA)}</span></div>
                  <div className="flex gap-4 border-t pt-1 mt-1"><span className="font-semibold">Total TTC</span><span className="font-semibold">{formatMoney(totalTTC)}</span></div>
                </div>
              </div>

              {/* Payment section */}
              {!po.pay && (
                <div className="mt-6 border border-gray-200 rounded-xl p-4 bg-gray-50">
                  <h3 className="text-sm font-semibold text-navy-700 mb-3">Paiements</h3>

                  {/* Payments list */}
                  {payments.length > 0 ? (
                    <div className="mb-4 space-y-2">
                      {payments.map((p: any, i: number) => (
                        <div key={i} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs">
                          <div className="flex items-center gap-4">
                            <span className="font-semibold text-gray-900">{Number(p.amount).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
                            <span className="text-gray-500">{p.date ? new Date(p.date).toLocaleDateString('fr-FR') : '—'}</span>
                            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{p.method || 'virement'}</span>
                            {p.note && <span className="text-gray-400 italic">{p.note}</span>}
                          </div>
                          <button
                            onClick={() => handleRemovePayment(i)}
                            className="text-red-400 hover:text-red-600 text-[11px] font-medium ml-2"
                          >
                            Supprimer
                          </button>
                        </div>
                      ))}
                      <div className="flex justify-between items-center text-xs pt-2 border-t border-gray-200">
                        <span className="text-gray-500">Total payé</span>
                        <span className="font-semibold text-emerald-700">
                          {payments.reduce((s: number, p: any) => s + Number(p.amount), 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-500">Reste à payer</span>
                        <span className="font-semibold text-amber-700">
                          {Math.max(0, totalTTC - payments.reduce((s: number, p: any) => s + Number(p.amount), 0)).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mb-3">Aucun paiement enregistré.</p>
                  )}

                  {/* Toggle add payment form */}
                  {!showPayForm ? (
                    <button
                      onClick={() => setShowPayForm(true)}
                      className="mt-1 flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-[#0260CB] text-[#0260CB] rounded-lg text-xs hover:bg-blue-50 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                      Ajouter un paiement
                    </button>
                  ) : (
                    <div className="mt-2 border border-blue-100 rounded-lg p-3 bg-white">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          placeholder="Montant (€)"
                          value={newPayAmount}
                          onChange={(e) => setNewPayAmount(e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-brand-500"
                          autoFocus
                        />
                        <input
                          type="date"
                          value={newPayDate}
                          onChange={(e) => setNewPayDate(e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-brand-500"
                        />
                        <select
                          value={newPayMethod}
                          onChange={(e) => setNewPayMethod(e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-brand-500 bg-white"
                        >
                          <option value="virement">Virement</option>
                          <option value="chèque">Chèque</option>
                          <option value="carte">Carte</option>
                          <option value="espèces">Espèces</option>
                          <option value="prélèvement">Prélèvement</option>
                          <option value="autre">Autre</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Note (optionnel)"
                          value={newPayNote}
                          onChange={(e) => setNewPayNote(e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-brand-500"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { handleAddPayment(); setShowPayForm(false); }}
                          disabled={savingPayment || !newPayAmount}
                          className="px-3 py-1.5 bg-[#0260CB] text-white rounded-lg text-xs hover:bg-[#01499b] disabled:opacity-50"
                        >
                          {savingPayment ? 'Enregistrement…' : 'Enregistrer'}
                        </button>
                        <button
                          onClick={() => { setShowPayForm(false); setNewPayAmount(''); setNewPayDate(new Date().toISOString().slice(0, 10)); setNewPayNote(''); }}
                          className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs hover:bg-gray-50"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {po.pay && (
                <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-xs text-emerald-800">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    <span className="font-semibold">Commande payée définitivement</span>
                    <span className="ml-1 text-emerald-600">— Total : {payments.reduce((s: number, p: any) => s + Number(p.amount), 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
                  </div>
                  {payments.length > 0 && (
                    <div className="space-y-1.5">
                      {payments.map((p: any, i: number) => (
                        <div key={i} className="flex items-center justify-between bg-white border border-emerald-100 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-4">
                            <span className="font-semibold text-gray-900">{Number(p.amount).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
                            <span className="text-gray-500">{p.date ? new Date(p.date).toLocaleDateString('fr-FR') : '—'}</span>
                            <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{p.method || 'virement'}</span>
                            {p.note && <span className="text-gray-400 italic">{p.note}</span>}
                          </div>
                          <button
                            onClick={() => handleRemovePayment(i)}
                            className="text-red-400 hover:text-red-600 text-[11px] font-medium ml-2 flex-shrink-0"
                            title="Supprimer ce paiement (réouvre la commande)"
                          >
                            Supprimer
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap justify-between gap-3 pt-4 border-t border-gray-200">
                <div className="flex gap-3">
                  {canCancelReception && (
                    <button
                      onClick={onCancelOpen}
                      disabled={cancellingReception}
                      className="px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 text-xs flex items-center gap-2 disabled:opacity-50"
                      title="Retirer du stock les quantités réceptionnées et repasser la commande en cours"
                    >
                      ↩ Annuler la réception
                    </button>
                  )}
                  {canDelete && (
                    <button 
                      onClick={onDeleteOpen} 
                      className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 text-xs flex items-center gap-2"
                    >
                      Supprimer la commande
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  {!po.pay && (
                    <button onClick={handleMarkPaid} disabled={markingPaid} className="px-4 py-2 rounded-lg text-xs flex items-center gap-2 disabled:opacity-50" style={{ backgroundColor: '#10b981', color: '#ffffff' }}>
                      {markingPaid ? 'Marquage…' : '✓ Solder & marquer payé'}
                    </button>
                  )}

                  {/* Partial reception button: only for pending/partial status */}
                  {(po.status === 'pending' || po.status === 'partial') && !isLocked && (
                    <button
                      onClick={handleMarkPartialReceived}
                      disabled={markingPartial || markingReceived}
                      className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2 text-xs disabled:opacity-50"
                      title="Réceptionner les quantités indiquées dans la colonne 'Qté à réceptionner'"
                    >
                      {markingPartial ? 'Enregistrement…' : '⬇ Réception partielle'}
                    </button>
                  )}

                  <button
                    onClick={po.status === 'pending' || po.status === 'partial' ? handleMarkReceived : handleSaveReception}
                    disabled={((po.status === 'pending' || po.status === 'partial') ? markingReceived || savingReception || markingPartial : savingReception) || isLocked}
                    className="px-4 py-2 bg-[#0260CB] text-white rounded-lg hover:bg-[#01499b] flex items-center gap-2 text-xs disabled:opacity-50"
                  >
                    {(po.status === 'pending' || po.status === 'partial') ? (markingReceived ? 'Enregistrement…' : '✓ Tout marquer reçu') : (savingReception ? 'Mise à jour…' : 'Mettre à jour réception')}
                  </button>

                  <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs">Fermer</button>
                </div>
              </div>
            </>
            )}
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Modal - rendered outside to be on top */}
      <DeletePurchase
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        context="de la commande d'achat"
        description={`Êtes-vous sûr de vouloir supprimer définitivement le bon de commande ${po?.purchaseId || ''} ? Cette action est irréversible.`}
        deleteFunction={handleDeletePurchase}
      />

      {/* Cancel reception confirmation — rendered outside to be on top */}
      {isCancelOpen && (
        <div className="fixed inset-0 z-[20000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { if (!cancellingReception) onCancelClose(); }} />
          <div className="relative w-full max-w-[640px]">
            <Card extra="px-[30px] pt-[35px] pb-[40px] flex flex-col">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a4 4 0 014 4v2m-14-6l4-4m-4 4l4 4" /></svg>
                </div>
                <h1 className="text-2xl font-bold">Annuler la réception</h1>
              </div>
              <p className="text-sm text-gray-600">
                Cette action retire du stock toutes les quantités réceptionnées pour la commande{' '}
                <span className="font-semibold text-gray-900">{po?.purchaseId}</span> (sur leur lot et zone d'origine),
                puis repasse la commande au statut « En cours ».
              </p>
              <ul className="mt-3 text-xs text-gray-500 list-disc list-inside space-y-1">
                <li>Un mouvement de stock « annulation-réception » est tracé pour chaque ligne.</li>
                <li>Si une quantité a déjà été consommée, le stock du lot peut devenir négatif (à régulariser).</li>
                <li>Les paiements enregistrés ne sont pas modifiés.</li>
              </ul>
              <div className="mt-5 flex gap-2 justify-end">
                <button
                  onClick={onCancelClose}
                  disabled={cancellingReception}
                  className="linear rounded-xl border-2 border-gray-700 px-5 py-3 text-base font-medium text-gray-700 transition duration-200 hover:bg-gray-100 disabled:opacity-50"
                >
                  Retour
                </button>
                <button
                  onClick={handleCancelReception}
                  disabled={cancellingReception}
                  className="linear flex items-center justify-center rounded-xl bg-amber-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-amber-600 active:bg-amber-700 disabled:opacity-50"
                >
                  {cancellingReception ? 'Annulation…' : "Confirmer l'annulation"}
                </button>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Price propagation proposal banner */}
      {showPriceProposalBanner && priceProposals.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[30000] w-full max-w-2xl px-4">
          <div className="bg-white border border-amber-300 rounded-2xl shadow-2xl p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 mb-1">Variation de prix d'achat détectée</p>
                <p className="text-xs text-gray-600 mb-2">
                  Souhaitez-vous appliquer le même pourcentage d'augmentation ou de diminution sur les prix de vente et les prix personnalisés ?
                </p>
                <div className="flex flex-col gap-1">
                  {priceProposals.map((p) => (
                    <div key={p.key} className="flex items-center gap-2 text-xs text-gray-700">
                      <span className="font-medium truncate max-w-[200px]">{p.productName}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500">{p.oldPurchasePrice.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} → {p.newPurchasePrice.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
                      <span className={`font-semibold ${ p.pct >= 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {p.pct >= 0 ? '+' : ''}{p.pct.toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Custom pricing toggle */}
            <div className="mt-2 mb-3 pt-2 border-t border-amber-100">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={applyToCustomPricings}
                  onChange={(e) => setApplyToCustomPricings(e.target.checked)}
                  className="w-3.5 h-3.5 accent-amber-500"
                />
                <span className="text-xs text-gray-700">
                  Mettre à jour aussi les tarifs personnalisés
                  {loadingCustomPricings ? (
                    <span className="ml-1 text-gray-400 italic">chargement…</span>
                  ) : customPricingProposals.length > 0 ? (
                    <span className="ml-1 font-semibold text-amber-700">({customPricingProposals.length} tarif{customPricingProposals.length > 1 ? 's' : ''} concerné{customPricingProposals.length > 1 ? 's' : ''})</span>
                  ) : (
                    <span className="ml-1 text-gray-400">(aucun tarif personnalisé trouvé)</span>
                  )}
                </span>
              </label>
              {applyToCustomPricings && customPricingProposals.length > 0 && (
                <div className="mt-1.5 ml-5 max-h-28 overflow-y-auto flex flex-col gap-0.5">
                  {customPricingProposals.map((cp) => (
                    <div key={cp.id} className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="truncate max-w-[160px] font-medium">{cp.productName}</span>
                      <span className="text-gray-400">·</span>
                      {cp.tags.length > 0 && (
                        <span className="text-[#01499b] font-medium">{cp.tags.join(', ')}</span>
                      )}
                      {cp.customerId && (
                        <span className="text-purple-600 font-medium">client spécifique</span>
                      )}
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500">{cp.oldPrice.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} → {cp.newPrice.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
                      <span className={`font-semibold ${cp.pct >= 0 ? 'text-red-600' : 'text-emerald-600'}`}>{cp.pct >= 0 ? '+' : ''}{cp.pct.toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  // Update baseline so ignored products don't re-trigger the banner
                  setPo((prev: any) => {
                    if (!prev?.items) return prev;
                    const updatedItems = prev.items.map((item: any) => {
                      const ignored = priceProposals.find((p) => p.key === item.__key);
                      if (!ignored) return item;
                      return { ...item, price: prices[item.__key] ?? item.price };
                    });
                    return { ...prev, items: updatedItems };
                  });
                  setShowPriceProposalBanner(false);
                  setPriceProposals([]);
                  setCustomPricingProposals([]);
                }}
                className="px-4 py-1.5 rounded-lg border border-gray-300 text-xs text-gray-600 hover:bg-gray-50"
              >
                Non, ignorer
              </button>
              <button
                onClick={handleApplyPriceProposals}
                disabled={applyingPriceProposal}
                className="px-4 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600 disabled:opacity-50"
              >
                {applyingPriceProposal ? 'Application…' : 'Oui, appliquer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {pdfViewerOpen && pdfUrl && (
        <div className="fixed inset-0 z-[20000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setPdfViewerOpen(false)} />
          <div className="relative z-10 w-full max-w-4xl h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <MdPictureAsPdf className="w-5 h-5 text-red-500" />
                <span className="font-semibold text-sm text-navy-700">Facture PDF — {po?.purchaseId}</span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-xs text-gray-600 hover:bg-gray-50"
                >
                  <MdOpenInNew className="w-4 h-4" />
                  Ouvrir dans un nouvel onglet
                </a>
                <a
                  href={pdfUrl}
                  download
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-xs text-gray-600 hover:bg-gray-50"
                >
                  <MdDownload className="w-4 h-4" />
                  Télécharger
                </a>
                <button
                  onClick={() => setPdfViewerOpen(false)}
                  className="text-gray-400 hover:text-gray-600 ml-1"
                >
                  <MdClose className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={pdfUrl}
                title="Facture PDF"
                className="w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UpdatePurchaseModal;
